// animations.tsx
import { Variants } from "framer-motion";

// -------------------- Simple Variants --------------------

export const navVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -50,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 140,
    },
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
      delay: 1,
    },
  },
};

export const textContainer: Variants = {
  hidden: { opacity: 0 },
  show: (i: number = 1) => ({
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: i * 0.1,
    },
  }),
};

export const textVariant2: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(16px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      type: "tween",
      ease: "easeIn",
    },
  },
};

export const footerVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 50,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 140,
    },
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
      delay: 0.5,
    },
  },
};

// -------------------- Helper Functions --------------------

type Direction = "left" | "right" | "up" | "down";
type TransitionType = "spring" | "tween";

export const slideIn = (
  direction: "left" | "right" | "up" | "down" = "up",
  type: "spring" | "tween" = "spring",
  delay = 0,
  duration = 0.5
): Variants => {
  let x = 0,
    y = 0;
  if (direction === "left") x = -40;
  if (direction === "right") x = 40;
  if (direction === "up") y = 40;
  if (direction === "down") y = -40;

  return {
    hidden: { opacity: 0, x, y },
    show: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type,
        delay,
        duration,
        ease: type === "tween" ? "easeOut" : undefined,
      },
    },
  };
};

export const staggerContainer = (
  staggerChildren: number,
  delayChildren: number
): Variants => ({
  hidden: {},
  show: {
    transition: {
      staggerChildren,
      delayChildren,
    },
  },
});

export const textVariant = (delay: number): Variants => ({
  hidden: { y: 50, opacity: 0, filter: "blur(16px)" },
  show: {
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
    transition: {
      type: "spring",
      duration: 1.25,
      delay,
    },
  },
});

export const fadeIn = (
  direction: Direction,
  type: TransitionType,
  delay: number,
  duration: number
): Variants => ({
  hidden: {
    x: direction === "left" ? 100 : direction === "right" ? -100 : 0,
    y: direction === "up" ? 100 : direction === "down" ? -100 : 0,
    opacity: 0,
    filter: "blur(16px)", // <-- add blur for lazy effect
  },
  show: {
    x: 0,
    y: 0,
    opacity: 1,
    filter: "blur(0px)", // <-- animate to sharp
    transition: {
      type,
      delay,
      duration,
      ease: "easeOut",
    },
  },
});

export const planetVariants = (direction: "left" | "right"): Variants => ({
  hidden: {
    x: direction === "left" ? "-100%" : "100%",
    rotate: 120,
  },
  show: {
    x: 0,
    rotate: 0,
    transition: {
      type: "spring",
      duration: 1.8,
      delay: 0.5,
    },
  },
});

export const zoomIn = (delay: number, duration: number): Variants => ({
  hidden: {
    scale: 0,
    opacity: 0,
    filter: "blur(16px)",
  },
  show: {
    scale: 1,
    opacity: 1,
    filter: "blur(0px)",
    transition: {
      type: "tween",
      delay,
      duration,
      ease: "easeOut",
    },
  },
});
